import { useCallback, useState, useEffect, useRef } from 'react';
import { useConversation } from '@elevenlabs/react';
import { useSelector, useDispatch } from 'react-redux';
import { IRootReducerState } from '@/store/reducers';
import { setElevenLabsConnectionStatus, setElevenLabsSessionId } from '@/store/actions/projectdata.action';
import { CommonService } from '@/services';

interface VoiceMessage {
    id: string;
    source: 'user' | 'ai';
    message: string;
    timestamp: Date;
    isTyping?: boolean;
}

interface UseElevenLabsConversationOptions {
    projectId?: string;
    agentId?: string;
    isRequirementGathering?: boolean;
    onTextMessage?: (messageData: VoiceMessage) => void;
    onVoiceStatusChange?: (isSpeaking: boolean, isListening: boolean) => void;
    onAudioInputVolumeChange?: (volume: number) => void;
}

interface UseElevenLabsConversationReturn {
    // State
    isTextOnlyMode: boolean;
    isConnected: boolean;
    isConnecting: boolean;
    isListening: boolean;
    isSpeaking: boolean;
    messages: VoiceMessage[];
    audioInputVolume: number;
    textMessage: string;
    isSendingTextMessage: boolean;
    volume: number;

    // Methods
    startTextConversation: () => Promise<void>;
    startVoiceConversation: () => Promise<void>;
    stopConversation: () => Promise<void>;
    sendTextMessage: (message?: string) => Promise<void>;
    setTextMessage: (message: string) => void;
    setVolume: (volume: number) => void;
    sendUserActivity: () => void;
    sendContextualUpdate: (context: string) => void;
    toggleMute: () => void;
    isMuted: boolean;
}

export const useElevenLabsConversation = ({
    projectId,
    agentId,
    isRequirementGathering = false,
    onTextMessage,
    onVoiceStatusChange,
    onAudioInputVolumeChange,
}: UseElevenLabsConversationOptions): UseElevenLabsConversationReturn => {
    const dispatch = useDispatch();

    // Get auth token from Redux state
    const { token: authToken } = useSelector((state: IRootReducerState) => state.auth);

    // State management - Track current mode
    const [isTextOnlyMode, setIsTextOnlyMode] = useState<boolean | null>(null); // null = not started yet
    const [isConnecting, setIsConnecting] = useState(false);
    const [isListening, setIsListening] = useState(false);
    const [messages, setMessages] = useState<VoiceMessage[]>([]);
    const [audioInputVolume, setAudioInputVolume] = useState(0);
    const [textMessage, setTextMessage] = useState('');
    const [isSendingTextMessage, setIsSendingTextMessage] = useState(false);
    const [volume, setVolume] = useState(1.0);
    const [isMuted, setIsMuted] = useState(false);

    const conversationRef = useRef<any>(null);
    const volumeIntervalRef = useRef<NodeJS.Timeout | null>(null);


    const handleDisconnect = useCallback(() => {
        setIsConnecting(false);
        setIsListening(false);
        setMessages([]);
        setAudioInputVolume(0);
        setTextMessage('');
        setIsSendingTextMessage(false);
        setIsMuted(false);
        if (volumeIntervalRef.current) {
            clearInterval(volumeIntervalRef.current);
            volumeIntervalRef.current = null;
        }
        dispatch(setElevenLabsConnectionStatus('disconnected'));
    }, [dispatch]);

    // ElevenLabs conversation hook
    const conversation = useConversation({
        textOnly: isTextOnlyMode === true, // Only text if explicitly set to true
        volume,
        onConnect: () => {
            setIsConnecting(false);
            setIsListening(false);

            // Start volume tracking when connected (only for voice mode)
            if (!isTextOnlyMode) {
                if (volumeIntervalRef.current) {
                    clearInterval(volumeIntervalRef.current);
                }
                volumeIntervalRef.current = setInterval(() => {
                    if (conversation.getInputVolume) {
                        const volume = conversation.getInputVolume();
                        setAudioInputVolume(volume);
                        onAudioInputVolumeChange?.(volume);
                    }
                }, 100);
            }

            dispatch(setElevenLabsConnectionStatus('connected'));
        },
        onDisconnect: () => {
            setIsConnecting(false);
            setIsListening(false);
            handleDisconnect();
        },
        onError: () => {
            setIsConnecting(false);
            setIsListening(false);
            dispatch(setElevenLabsConnectionStatus('error'));
        },
        onMessage: (message) => {
            if (message && typeof message === 'object') {
                let messageText = (message as any).message || String(message);

                // Clean up AI messages - remove common unwanted prefixes and tags
                if ((message as any).source === 'ai' || !(message as any).source) {
                    messageText = messageText
                        .replace(/<yara\s*>/gi, '')
                        .replace(/<\/yara\s*>/gi, '')
                        .replace(/^\[AI\]\s*/i, '')
                        .replace(/^AI:\s*/i, '')
                        .replace(/^Assistant:\s*/i, '')
                        .replace(/^Bot:\s*/i, '')
                        .trim();
                }

                const voiceMessage: VoiceMessage = {
                    id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                    source: (message as any).source || 'ai',
                    message: messageText,
                    timestamp: new Date(),
                    isTyping: false
                };

                // Track AI message received from ElevenLabs
                if ((message as any).source === 'ai' || !(message as any).source) {
                    CommonService.handleMixpanelTrack("ElevenLabs AI Message Received", {
                        project_id: projectId,
                        message_length: messageText.length,
                        is_requirement_gathering: isRequirementGathering,
                        timestamp: new Date().toISOString()
                    });
                }

                // Check for requirements completion
                if (messageText.includes('REQUIREMENTS_COMPLETED: True') ||
                    messageText.includes('=== FINAL PROMPT FOR DEVELOPMENT AGENT ===')) {
                    if (conversationRef.current?.status === 'connected') {
                        conversationRef.current.endSession();
                    }
                    CommonService._communications.requirementsGatheringCompletedSubject.next({
                        message: messageText,
                        type: 'text'
                    });
                    return;
                }

                // If in requirement gathering mode, send messages to callback
                if (isRequirementGathering && onTextMessage && messageText) {
                    onTextMessage(voiceMessage);
                    return;
                }

                setMessages(prevMessages => [...prevMessages, voiceMessage]);
            }
        },
        onModeChange: (mode) => {
            // Only handle voice mode changes when not in text-only mode
            if (!isTextOnlyMode) {
                const isListeningNow = mode.mode === 'listening';
                setIsListening(isListeningNow);

                // Track voice input detection
                if (mode.mode === 'listening') {
                    CommonService.handleMixpanelTrack("ElevenLabs Voice Input Started", {
                        project_id: projectId,
                        is_requirement_gathering: isRequirementGathering,
                        timestamp: new Date().toISOString()
                    });
                }

                // Communicate voice status changes to parent component
                if (isRequirementGathering && onVoiceStatusChange) {
                    onVoiceStatusChange(mode.mode === 'speaking', isListeningNow);
                }
            }
        }
    });

    // Store conversation reference
    conversationRef.current = conversation;

    // Start TEXT conversation
    const startTextConversation = useCallback(async () => {

        if (!projectId || !authToken) {
            console.error('❌ Missing projectId or authToken');
            CommonService._alert.showToast('Authentication required', 'error');
            return;
        }

        try {
            // Set to text mode
            setIsTextOnlyMode(true);
            setIsConnecting(true);
            dispatch(setElevenLabsConnectionStatus('connecting'));


            // Prepare dynamic variables
            const dynamicVars = {
                secret__formattedAuthToken: `Bearer ${authToken}`,
                project_id: projectId
            };

            // Session options for text mode
            const sessionOptions: any = {
                agentId: agentId || 'agent_1401k2ywx6r0fqdvjj06dqx16jv3',
                userId: projectId,
                dynamicVariables: dynamicVars,
                textOnly: true // Explicitly set text-only mode
            };


            const conversationId = await conversation.startSession(sessionOptions);

            if (conversationId) {
                // Track successful conversation start
                CommonService.handleMixpanelTrack("ElevenLabs Conversation Started", {
                    project_id: projectId,
                    conversation_id: conversationId,
                    mode: 'text',
                    is_requirement_gathering: isRequirementGathering,
                    agent_id: agentId,
                    timestamp: new Date().toISOString()
                });

                dispatch(setElevenLabsSessionId(conversationId));
                dispatch(setElevenLabsConnectionStatus('connected'));
            }

        } catch (error: any) {
            setIsConnecting(false);
            dispatch(setElevenLabsConnectionStatus('error'));
            CommonService._alert.showToast(
                `Failed to start text conversation: ${error?.message || 'Unknown error'}`,
                'error'
            );
        }
    }, [conversation, projectId, authToken, agentId, isRequirementGathering, dispatch]);

    // Start VOICE conversation
    const startVoiceConversation = useCallback(async () => {

        if (!projectId || !authToken) {
            console.error('❌ Missing projectId or authToken');
            CommonService._alert.showToast('Authentication required', 'error');
            return;
        }

        try {
            // Set to voice mode
            setIsTextOnlyMode(false);
            setIsConnecting(true);
            dispatch(setElevenLabsConnectionStatus('connecting'));

            // Request microphone permission
            await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                    sampleRate: 16000,
                    channelCount: 1
                }
            });

            // Prepare dynamic variables
            const dynamicVars = {
                secret__formattedAuthToken: `Bearer ${authToken}`,
                project_id: projectId
            };

            // Session options for voice mode
            const sessionOptions: any = {
                agentId: agentId || 'agent_1401k2ywx6r0fqdvjj06dqx16jv3',
                userId: projectId,
                connectionType: isRequirementGathering ? 'websocket' : 'webrtc',
                dynamicVariables: dynamicVars,
                textOnly: false // Explicitly set voice mode
            };


            const conversationId = await conversation.startSession(sessionOptions);

            if (conversationId) {
                // Track successful conversation start
                CommonService.handleMixpanelTrack("ElevenLabs Conversation Started", {
                    project_id: projectId,
                    conversation_id: conversationId,
                    mode: 'voice',
                    is_requirement_gathering: isRequirementGathering,
                    agent_id: agentId,
                    timestamp: new Date().toISOString()
                });

                dispatch(setElevenLabsSessionId(conversationId));
                dispatch(setElevenLabsConnectionStatus('connected'));
            }

        } catch (error: any) {
            setIsConnecting(false);
            dispatch(setElevenLabsConnectionStatus('error'));

            if (error?.name === 'NotAllowedError') {
                CommonService._alert.showToast(
                    'Microphone permission required for voice chat',
                    'error'
                );
            } else if (error?.name === 'NotFoundError') {
                CommonService._alert.showToast(
                    'No microphone found. Please connect a microphone',
                    'error'
                );
            } else {
                CommonService._alert.showToast(
                    `Failed to start voice conversation: ${error?.message || 'Unknown error'}`,
                    'error'
                );
            }
        }
    }, [conversation, projectId, authToken, agentId, isRequirementGathering, dispatch]);

    // Stop conversation
    const stopConversation = useCallback(async () => {
        try {
            // Track conversation end
            CommonService.handleMixpanelTrack("ElevenLabs Conversation Ended", {
                project_id: projectId,
                mode: isTextOnlyMode === true ? 'text' : isTextOnlyMode === false ? 'voice' : 'unknown',
                is_requirement_gathering: isRequirementGathering,
                timestamp: new Date().toISOString()
            });

            // Reset all state variables to initial state
            setIsListening(false);
            setIsConnecting(false);
            setMessages([]);
            setAudioInputVolume(0);
            setTextMessage('');
            setIsSendingTextMessage(false);
            setIsTextOnlyMode(null); // Reset mode so user can select again
            setIsMuted(false);

            // Clear volume tracking interval
            if (volumeIntervalRef.current) {
                clearInterval(volumeIntervalRef.current);
                volumeIntervalRef.current = null;
            }

            // Disconnect from ElevenLabs
            dispatch(setElevenLabsConnectionStatus('disconnected'));
            await conversationRef.current?.endSession();
            dispatch(setElevenLabsSessionId(null));

        } catch (error: any) {
            console.error('❌ Error stopping conversation:', error);
            CommonService._alert.showToast('Error stopping conversation', 'error');
        }
    }, [dispatch, projectId, isTextOnlyMode, isRequirementGathering]);

    // Send text message
    const sendTextMessage = useCallback(async (message?: string) => {
        const messageToSend = message || textMessage;

        if (!messageToSend.trim() || !conversationRef.current || conversationRef.current.status !== 'connected') {
            return;
        }

        try {
            setIsSendingTextMessage(true);

            // Track text message sent to ElevenLabs
            CommonService.handleMixpanelTrack("ElevenLabs Text Message Sent", {
                project_id: projectId,
                message_length: messageToSend.trim().length,
                is_requirement_gathering: isRequirementGathering,
                timestamp: new Date().toISOString()
            });

            // Add user message to local state
            const userMessage: VoiceMessage = {
                id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                source: 'user',
                message: messageToSend.trim(),
                timestamp: new Date(),
                isTyping: false
            };
            setMessages(prevMessages => [...prevMessages, userMessage]);

            // Send text message to ElevenLabs agent
            conversationRef.current.sendUserMessage(messageToSend.trim());

            // Clear input if using state
            if (!message) {
                setTextMessage('');
            }
        } catch (error) {
            console.error('❌ Error sending text message:', error);
            CommonService._alert.showToast('Failed to send message', 'error');
        } finally {
            setIsSendingTextMessage(false);
        }
    }, [textMessage, projectId, isRequirementGathering]);

    // Send user activity
    const sendUserActivity = useCallback(() => {
        if (conversationRef.current?.status === 'connected' && conversationRef.current.sendUserActivity) {
            conversationRef.current.sendUserActivity();
        }
    }, []);

    // Send contextual update
    const sendContextualUpdate = useCallback((context: string) => {
        if (conversationRef.current?.status === 'connected' && conversationRef.current.sendContextualUpdate) {
            conversationRef.current.sendContextualUpdate(context);
        }
    }, []);

    // Listen to ElevenLabs communications service
    useEffect(() => {
        const subscription = CommonService._communications.elevenLabsSendMessageSubject.subscribe((messageData) => {


            if (messageData.type === 'start-text') {
                startTextConversation();
            } else if (messageData.type === 'start-voice') {
                startVoiceConversation();
            } else if (messageData.type === 'disconnect') {
                if (conversationRef.current?.status === 'connected') {
                    conversationRef.current.endSession();
                }
            } else if (conversationRef.current?.status === 'connected') {
                if (messageData.type === 'text' && messageData.message) {
                    conversationRef.current.sendUserMessage(messageData.message);
                } else if (messageData.type === 'activity') {
                    conversationRef.current.sendUserActivity();
                }
            }
        });

        return () => {
            subscription.unsubscribe();
        };
    }, [startTextConversation, startVoiceConversation]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (conversationRef.current?.status === 'connected') {
                conversationRef.current.endSession();
            }
            if (volumeIntervalRef.current) {
                clearInterval(volumeIntervalRef.current);
            }
        };
    }, []);

    // Update volume when state changes
    useEffect(() => {
        if (conversationRef.current?.setVolume && !isTextOnlyMode) {
            conversationRef.current.setVolume({ volume });
        }
    }, [volume, isTextOnlyMode]);

    // Handle Mute Toggle
    const toggleMute = useCallback(() => {
        setIsMuted(prev => !prev);
    }, []);

    // Effect to apply mute state
    useEffect(() => {
        // According to documentation, we might pass micMuted to options to update it?
        // But since useConversation options might not be reactive, we should check for setMicMuted method.
        // Wait, the documentation search said `setMicMuted` is returned by useConversation. 
        // IF it is NOT returned, we might fail here. 
        // Let's assume (based on search) that we can use it if available, OR try to pass it as option.
        // Actually, let's look at the `conversation` object type which is `any` now in `useRef`.

        if (conversationRef.current && conversationRef.current.status === 'connected') {
            // Check if setMicMuted exists
            if (typeof conversationRef.current.setMicMuted === 'function') {
                conversationRef.current.setMicMuted(isMuted);
            } else {
                // Fallback: maybe it's a property we can set on options if useConversation re-renders?
                // But useConversation is called with options. If I update options, it might trigger update.
                // However, I cannot easily change the hook call inside this effect.
                // For now, I'll rely on setMicMuted presence.
                console.log('Mute toggled:', isMuted);
            }
        }
    }, [isMuted]);

    return {
        // State
        isTextOnlyMode: isTextOnlyMode === true,
        isConnected: conversation.status === 'connected',
        isConnecting,
        isListening,
        isSpeaking: conversation.isSpeaking || false,
        messages,
        audioInputVolume,
        textMessage,
        isSendingTextMessage,
        volume,

        isMuted,

        // Methods
        startTextConversation,
        startVoiceConversation,
        stopConversation,
        sendTextMessage,
        setTextMessage,
        setVolume,
        sendUserActivity,
        sendContextualUpdate,
        toggleMute,
    };
};

